import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../../store";
import {
  setPatients,
  setLoading,
  setError,
} from "../../store/slices/dashboardSlice";
import { selectSelectedProviderIds } from "../../store/selectors/dashboardSelectors";
import PatientsSearch from "./PatientsSearch";
import PatientsTabs from "./PatientsTabs";
import PatientsTable from "./PatientsTable";
import {
  PatientsListCard,
  PatientsListHeader,
  PatientsListTitle,
} from "./DashboardPatientsList.styled";
import { GetPatientsParams, patientsService } from "../../services/patientsService";
import { TreatmentPlanStatus } from "../../types/treatmentPlan";

// Map frontend field names to API field names (outside component to prevent re-creation)
const FIELD_TO_API_MAPPING: Record<string, string> = {
  patientFirstName: "PatientName",
  providerFirstName: "ProviderName",
  createdAt: "CreatedAt",
};

interface PatientsListProps {
  className?: string;
}

const statusFiltersData = {
  1: [TreatmentPlanStatus.AIDrafted, TreatmentPlanStatus.AwaitingProviderReview],
  2: [TreatmentPlanStatus.AwaitingPatientConfirmation],
  3: [TreatmentPlanStatus.ApprovedByPatient],
}

const DashboardPatientsList: React.FC<PatientsListProps> = ({ className }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const selectedProviderIds = useSelector(selectSelectedProviderIds);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<1 | 2 | 3>(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(9);
  const [totalCount, setTotalCount] = useState(0);

  // Separate sort state for each tab
  const [tabSortState, setTabSortState] = useState<
    Record<1 | 2 | 3, { field: string; order: "asc" | "desc" }>
  >({
    1: { field: "createdAt", order: "desc" },
    2: { field: "createdAt", order: "desc" },
    3: { field: "createdAt", order: "desc" },
  });

  // Get current tab's sort state
  const sortField = tabSortState[activeTab].field;
  const sortOrder = tabSortState[activeTab].order;

  // AbortController ref to cancel previous requests
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchPatients = useCallback(async () => {
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const apiSortField = FIELD_TO_API_MAPPING[sortField] || sortField;

      const params: GetPatientsParams = {
        treatmentPlanStatuses: statusFiltersData[activeTab],
        ...(selectedProviderIds.length > 0 && {
          providerId: selectedProviderIds,
        }),
        search: searchTerm || undefined,
        start: (currentPage - 1) * pageSize,
        limit: pageSize,
        total: true,
        orderBy: apiSortField,
        order: sortOrder === "asc" ? 0 : 1,
      };

      const response = await patientsService.getPatients(params);

      dispatch(setPatients(response.data || []));
      setTotalCount(response.total || 0);
    } catch (err) {
      // Ignore aborted requests
      if (err instanceof Error && err.name === "CanceledError") {
        return;
      }
      dispatch(setError("Failed to load patients"));
      console.error("Error fetching patients:", err);
    } finally {
      // Only update loading if this request wasn't aborted
      if (!abortController.signal.aborted) {
        dispatch(setLoading(false));
      }
    }
  }, [
    dispatch,
    activeTab,
    searchTerm,
    sortField,
    sortOrder,
    currentPage,
    pageSize,
    selectedProviderIds,
  ]);

  useEffect(() => {
    fetchPatients();

    // Cleanup: abort request on unmount or before next fetch
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchPatients]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
      setCurrentPage(1);
    },
    [],
  );

  const handleTabChange = useCallback((tab: 1 | 2 | 3) => {
    setActiveTab(tab);
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback(
    (field: string, direction: "asc" | "desc") => {
      setTabSortState((prev) => ({
        ...prev,
        [activeTab]: { field, order: direction },
      }));
      setCurrentPage(1);
    },
    [activeTab],
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePatientClick = useCallback(
    (patientId: number) => {
      navigate(`/patients/${patientId}`);
    },
    [navigate],
  );

  const handleTreatmentPlanClick = useCallback(
    (treatmentPlanId: number) => {
      navigate(`/treatment-plan/${treatmentPlanId}`);
    },
    [navigate],
  );

  const handleProviderClick = useCallback(
    (providerId: number) => {
      navigate(`/user-management/${providerId}`);
    },
    [navigate],
  );

  return (
    <PatientsListCard className={className}>
      <PatientsListHeader>
        <PatientsListTitle>Patients List</PatientsListTitle>
        <PatientsSearch
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />
      </PatientsListHeader>

      <PatientsTabs activeTab={activeTab} onTabChange={handleTabChange} />

      <PatientsTable
        activeTab={activeTab}
        searchTerm={searchTerm}
        sortBy={sortField}
        sortOrder={sortOrder}
        currentPage={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onPatientClick={handlePatientClick}
        onProviderClick={handleProviderClick}
        onTreatmentPlanClick={handleTreatmentPlanClick}
        onRetry={fetchPatients}
      />
    </PatientsListCard>
  );
};

export default DashboardPatientsList;
